import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  ShieldCheck,
  Lock,
  Bug,
  WifiOff,
  CheckCircle2,
  ShoppingCart,
  Bell,
  Package,
  ScanLine,
  BarChart3,
  ArrowRight,
  Check,
  Search,
  MonitorSmartphone,
  Star,
  Apple,
  Play,
  Home,
  Activity,
  User,
  Footprints,
  Flame,
  Heart,
} from "lucide-react";
import { Button } from "../ui/button";

const scrollToContact = () => {
  const el = document.querySelector("#contact");
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const IMG = {
  storeHero: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&q=80&auto=format&fit=crop",
  watch: "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&q=80&auto=format&fit=crop",
  shoe: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&q=80&auto=format&fit=crop",
  headphones: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400&q=80&auto=format&fit=crop",
  avatarWoman: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&q=80&auto=format&fit=crop",
  avatarMan: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&q=80&auto=format&fit=crop",
  workout: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&q=80&auto=format&fit=crop",
  gym: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=600&q=80&auto=format&fit=crop",
};

/* ------------------------------------------------------------------ */
/* Panel 1 — Cyber Security                                            */
/* ------------------------------------------------------------------ */
const SecurityAnimation = ({ reduced }) => (
  <div className="relative w-full h-[320px] md:h-[360px] flex items-center justify-center overflow-hidden">
    {!reduced && (
      <motion.div
        className="absolute h-64 w-64 md:h-72 md:w-72 rounded-full"
        style={{
          background:
            "conic-gradient(from 0deg, rgba(34,211,238,0.28), transparent 70deg, transparent 360deg)",
        }}
        animate={{ rotate: 360 }}
        transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
      />
    )}
    <div className="absolute h-64 w-64 md:h-72 md:w-72 rounded-full border border-primary/20" />
    <div className="absolute h-44 w-44 md:h-52 md:w-52 rounded-full border border-primary/15" />
    <div className="absolute h-24 w-24 rounded-full border border-primary/10" />

    {!reduced &&
      [0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="absolute h-20 w-20 rounded-full border-2 border-primary/40"
          animate={{ scale: [1, 3.2], opacity: [0.55, 0] }}
          transition={{ duration: 2.8, repeat: Infinity, delay: i * 0.9, ease: "easeOut" }}
        />
      ))}

    <motion.div
      className="relative z-10 h-20 w-20 rounded-2xl bg-primary/15 border border-primary/40 backdrop-blur flex items-center justify-center shadow-[0_0_40px_rgba(34,211,238,0.25)]"
      animate={reduced ? {} : { y: [0, -6, 0] }}
      transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
    >
      <ShieldCheck className="h-10 w-10 text-primary" />
    </motion.div>

    {!reduced &&
      [
        { Icon: Bug, from: { x: -150, y: -90 }, delay: 0 },
        { Icon: WifiOff, from: { x: 160, y: -70 }, delay: 1.6 },
        { Icon: Bug, from: { x: 140, y: 100 }, delay: 3.2 },
      ].map(({ Icon, from, delay }, i) => (
        <motion.div
          key={i}
          className="absolute h-9 w-9 rounded-lg bg-destructive/15 border border-destructive/40 flex items-center justify-center"
          initial={{ x: from.x, y: from.y, opacity: 0, scale: 1 }}
          animate={{
            x: [from.x, from.x * 0.35, from.x * 0.3],
            y: [from.y, from.y * 0.35, from.y * 0.3],
            opacity: [0, 1, 0],
            scale: [1, 1, 0.4],
          }}
          transition={{ duration: 4.8, repeat: Infinity, delay, times: [0, 0.7, 1] }}
        >
          <Icon className="h-5 w-5 text-destructive" />
        </motion.div>
      ))}

    <div className="absolute bottom-3 left-3 space-y-1.5">
      {["Firewall active", "Threat blocked", "SSL secured"].map((label, i) => (
        <motion.div
          key={label}
          className="flex items-center gap-1.5 text-xs text-muted-foreground bg-black/40 border border-white/10 rounded-full px-2.5 py-1 backdrop-blur"
          animate={reduced ? {} : { opacity: [0.45, 1, 0.45] }}
          transition={{ duration: 3, repeat: Infinity, delay: i * 1 }}
        >
          <CheckCircle2 className="h-3 w-3 text-emerald-400" /> {label}
        </motion.div>
      ))}
    </div>

    <motion.div
      className="absolute top-4 right-4 h-9 w-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center"
      animate={reduced ? {} : { y: [0, 8, 0], rotate: [0, -6, 0] }}
      transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
    >
      <Lock className="h-4 w-4 text-primary" />
    </motion.div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Panel 2 — Selling Online                                            */
/* ------------------------------------------------------------------ */
const CommerceAnimation = ({ reduced }) => (
  <div className="relative w-full h-[320px] md:h-[360px] overflow-hidden p-5">
    <div className="absolute left-5 right-5 top-5 bottom-24 rounded-xl bg-white/[0.04] border border-white/10 p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Sales this month</span>
        <motion.span
          className="text-xs font-semibold text-emerald-400"
          animate={reduced ? {} : { opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 2.5, repeat: Infinity }}
        >
          +128%
        </motion.span>
      </div>
      <svg viewBox="0 0 300 110" className="w-full h-[70%] mt-2" preserveAspectRatio="none">
        <defs>
          <linearGradient id="salesFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(34,211,238,0.35)" />
            <stop offset="100%" stopColor="rgba(34,211,238,0)" />
          </linearGradient>
        </defs>
        <motion.path
          d="M0,95 C40,88 55,70 85,72 C115,74 130,50 165,48 C200,46 215,30 245,24 C265,20 285,12 300,8"
          fill="none"
          stroke="#22d3ee"
          strokeWidth="2.5"
          initial={{ pathLength: reduced ? 1 : 0 }}
          whileInView={reduced ? {} : { pathLength: 1 }}
          viewport={{ once: false, amount: 0.4 }}
          transition={{ duration: 2.2, ease: "easeInOut" }}
        />
        <path
          d="M0,95 C40,88 55,70 85,72 C115,74 130,50 165,48 C200,46 215,30 245,24 C265,20 285,12 300,8 L300,110 L0,110 Z"
          fill="url(#salesFill)"
          opacity="0.6"
        />
      </svg>
    </div>

    {!reduced &&
      [
        { text: "New Amazon order — £49.99", top: "18%", delay: 0 },
        { text: "eBay item sold — £23.50", top: "38%", delay: 1.8 },
        { text: "Shopify checkout — £89.00", top: "58%", delay: 3.6 },
      ].map((order, i) => (
        <motion.div
          key={i}
          className="absolute right-8 z-10 flex items-center gap-2 rounded-xl bg-black/60 border border-white/15 px-3 py-2 text-xs text-foreground backdrop-blur shadow-lg"
          style={{ top: order.top }}
          animate={{ opacity: [0, 1, 1, 0], x: [24, 0, 0, -12] }}
          transition={{ duration: 5.4, repeat: Infinity, delay: order.delay, times: [0, 0.12, 0.85, 1] }}
        >
          <Bell className="h-3.5 w-3.5 text-accent" /> {order.text}
        </motion.div>
      ))}

    <motion.div
      className="absolute bottom-16 left-8 h-11 w-11 rounded-xl bg-accent/15 border border-accent/40 flex items-center justify-center z-10"
      animate={reduced ? {} : { y: [0, -5, 0] }}
      transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
    >
      <ShoppingCart className="h-5 w-5 text-accent" />
    </motion.div>

    <div className="absolute bottom-4 left-5 right-5 flex flex-wrap gap-2 justify-center">
      {["Amazon", "eBay", "Etsy", "Shopify"].map((platform, i) => (
        <motion.span
          key={platform}
          className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-muted-foreground"
          animate={
            reduced
              ? {}
              : { borderColor: ["rgba(255,255,255,0.1)", "rgba(34,211,238,0.5)", "rgba(255,255,255,0.1)"], y: [0, -3, 0] }
          }
          transition={{ duration: 3.2, repeat: Infinity, delay: i * 0.8 }}
        >
          {platform}
        </motion.span>
      ))}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Panel 3 — Website Development (browser mockup + real store site)    */
/* ------------------------------------------------------------------ */
const WebsiteAnimation = ({ reduced }) => (
  <div className="relative w-full h-[360px] md:h-[420px] overflow-hidden p-4 md:p-6">
    {/* Browser frame */}
    <div className="relative h-full max-w-[420px] mx-auto rounded-xl bg-[#0b0f16] border border-white/10 overflow-hidden shadow-2xl">
      {/* Chrome bar */}
      <div className="relative z-10 flex items-center gap-2 px-3 py-2 border-b border-white/10 bg-white/[0.04]">
        <span className="h-2.5 w-2.5 rounded-full bg-destructive/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-accent/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
        <div className="ml-2 flex-1 flex items-center gap-1.5 rounded-md bg-black/40 border border-white/10 px-2.5 py-1 text-[10px] text-muted-foreground">
          <Lock className="h-2.5 w-2.5 text-emerald-400" /> yourbusiness.co.uk
        </div>
      </div>

      {/* Auto-scrolling web page */}
      <motion.div
        animate={reduced ? {} : { y: [0, -270, 0] }}
        transition={{ duration: 13, repeat: Infinity, ease: "easeInOut", times: [0, 0.5, 1] }}
      >
        {/* Site nav */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-[#0e1420]">
          <span className="font-display text-[11px] font-bold tracking-widest text-foreground">AURA & CO</span>
          <div className="flex gap-3 text-[9px] text-muted-foreground">
            <span>Shop</span>
            <span>About</span>
            <span>Contact</span>
          </div>
        </div>
        {/* Hero */}
        <div className="relative h-28 overflow-hidden">
          <img src={IMG.storeHero} alt="Store hero" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 to-black/20 flex flex-col justify-center px-4">
            <p className="font-display text-sm font-semibold text-white leading-tight">New Season<br />Collection</p>
            <span className="mt-1.5 inline-flex w-fit rounded-full bg-primary text-primary-foreground text-[9px] font-semibold px-2.5 py-1">
              Shop Now
            </span>
          </div>
        </div>
        {/* Products */}
        <div className="px-4 py-3">
          <p className="text-[10px] font-semibold text-foreground mb-2">Best Sellers</p>
          <div className="grid grid-cols-3 gap-2">
            {[
              { img: IMG.watch, name: "Classic Watch", price: "£149" },
              { img: IMG.shoe, name: "Air Runner", price: "£89" },
              { img: IMG.headphones, name: "Studio Audio", price: "£199" },
            ].map((product) => (
              <div key={product.name} className="rounded-lg bg-white/[0.04] border border-white/10 overflow-hidden">
                <img src={product.img} alt={product.name} className="h-14 w-full object-cover" loading="lazy" />
                <div className="p-1.5">
                  <p className="text-[8px] text-foreground truncate">{product.name}</p>
                  <p className="text-[9px] font-semibold text-primary">{product.price}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Testimonial */}
        <div className="mx-4 mb-3 flex items-center gap-2.5 rounded-lg bg-white/[0.04] border border-white/10 p-2.5">
          <img src={IMG.avatarWoman} alt="Happy customer" className="h-7 w-7 rounded-full object-cover" loading="lazy" />
          <div>
            <div className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, s) => (
                <Star key={s} className="h-2 w-2 text-accent fill-accent" />
              ))}
            </div>
            <p className="text-[8px] text-muted-foreground mt-0.5">"Beautiful site, orders doubled in a month."</p>
          </div>
        </div>
        {/* Category strip */}
        <div className="px-4 pb-3">
          <p className="text-[10px] font-semibold text-foreground mb-2">Shop by Category</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { img: "https://images.unsplash.com/photo-1560343090-f0409e92791a?w=400&q=80&auto=format&fit=crop", name: "Footwear" },
              { img: "https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?w=400&q=80&auto=format&fit=crop", name: "Apparel" },
            ].map((cat) => (
              <div key={cat.name} className="relative h-16 rounded-lg overflow-hidden">
                <img src={cat.img} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-black/45 flex items-center justify-center">
                  <span className="text-[9px] font-semibold text-white tracking-wide">{cat.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Trust features */}
        <div className="px-4 pb-3 grid grid-cols-3 gap-2">
          {["Free Delivery", "Secure Checkout", "24/7 Support"].map((feat) => (
            <div key={feat} className="rounded-lg bg-white/[0.04] border border-white/10 py-2 text-center">
              <CheckCircle2 className="h-3 w-3 text-primary mx-auto" />
              <p className="text-[7px] text-muted-foreground mt-1">{feat}</p>
            </div>
          ))}
        </div>
        {/* Footer strip */}
        <div className="px-4 py-3 bg-[#0e1420] flex items-center justify-between">
          <span className="text-[8px] text-muted-foreground">© AURA & CO</span>
          <span className="text-[8px] text-primary">Built by FCY IT</span>
        </div>
      </motion.div>
    </div>

    {/* Floating performance score */}
    <motion.div
      className="absolute top-8 right-1 md:right-4 z-20 rounded-xl bg-black/70 border border-white/15 backdrop-blur p-2.5 flex flex-col items-center shadow-xl"
      animate={reduced ? {} : { y: [0, -7, 0] }}
      transition={{ duration: 3.8, repeat: Infinity, ease: "easeInOut" }}
    >
      <svg width="44" height="44" viewBox="0 0 44 44">
        <circle cx="22" cy="22" r="18" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="3.5" />
        <motion.circle
          cx="22"
          cy="22"
          r="18"
          fill="none"
          stroke="#34d399"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeDasharray="113"
          initial={{ strokeDashoffset: 113 }}
          whileInView={{ strokeDashoffset: 113 * 0.02 }}
          viewport={{ once: false, amount: 0.5 }}
          transition={{ duration: 1.8, ease: "easeOut" }}
          transform="rotate(-90 22 22)"
        />
        <text x="22" y="26" textAnchor="middle" fill="#34d399" fontSize="12" fontWeight="700">
          98
        </text>
      </svg>
      <span className="text-[9px] text-muted-foreground mt-0.5">Performance</span>
    </motion.div>

    {/* Floating chips */}
    <motion.div
      className="absolute bottom-8 left-1 md:left-3 z-20 flex items-center gap-1.5 rounded-full bg-black/70 border border-white/15 backdrop-blur px-3 py-1.5 text-[10px] text-foreground shadow-xl"
      animate={reduced ? {} : { y: [0, 6, 0] }}
      transition={{ duration: 4.4, repeat: Infinity, ease: "easeInOut" }}
    >
      <MonitorSmartphone className="h-3 w-3 text-primary" /> Responsive
    </motion.div>
    <motion.div
      className="absolute top-20 left-1 md:left-3 z-20 flex items-center gap-1.5 rounded-full bg-black/70 border border-white/15 backdrop-blur px-3 py-1.5 text-[10px] text-foreground shadow-xl"
      animate={reduced ? {} : { y: [0, -6, 0] }}
      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
    >
      <Search className="h-3 w-3 text-accent" /> SEO Ready
    </motion.div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Panel 4 — Mobile App Development (phone mockups + real app UI)      */
/* ------------------------------------------------------------------ */
const MobileAnimation = ({ reduced }) => (
  <div className="relative w-full h-[380px] md:h-[430px] flex items-center justify-center overflow-hidden">
    {/* Back phone */}
    <motion.div
      className="absolute w-[140px] h-[280px] rounded-[1.8rem] border-4 border-zinc-800/70 bg-[#0b0f16] -rotate-12 -translate-x-24 md:-translate-x-32 opacity-50 overflow-hidden"
      animate={reduced ? {} : { rotate: [-12, -10, -12] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
    >
      <div className="p-3 space-y-2 mt-4">
        <div className="h-2 w-16 rounded-full bg-white/15" />
        <div className="h-14 rounded-lg bg-primary/15 border border-primary/20" />
        <div className="h-2 w-20 rounded-full bg-white/10" />
        <div className="h-2 w-14 rounded-full bg-white/10" />
        <div className="h-10 rounded-lg bg-white/5 border border-white/10" />
        <div className="h-10 rounded-lg bg-white/5 border border-white/10" />
      </div>
    </motion.div>

    {/* Front phone */}
    <motion.div
      className="relative z-10 w-[175px] h-[350px] rounded-[2.1rem] border-[5px] border-zinc-800 bg-black overflow-hidden shadow-[0_25px_60px_rgba(0,0,0,0.6)]"
      animate={reduced ? {} : { y: [0, -9, 0] }}
      transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
    >
      {/* Notch */}
      <div className="absolute top-1.5 left-1/2 -translate-x-1/2 h-4 w-16 rounded-full bg-zinc-900 z-20" />
      {/* Status bar */}
      <div className="flex items-center justify-between px-4 pt-2.5 pb-1 text-[9px] text-foreground/80">
        <span>9:41</span>
        <div className="flex gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-foreground/60" />
          <span className="h-1.5 w-1.5 rounded-full bg-foreground/60" />
          <span className="h-1.5 w-1.5 rounded-full bg-foreground/60" />
        </div>
      </div>
      {/* App header */}
      <div className="flex items-center justify-between px-3.5 py-2">
        <div>
          <p className="text-[8px] text-muted-foreground">Good morning</p>
          <p className="font-display text-[11px] font-semibold text-foreground">FitPulse</p>
        </div>
        <img src={IMG.avatarMan} alt="App user" className="h-6 w-6 rounded-full object-cover border border-primary/40" loading="lazy" />
      </div>
      {/* Workout hero card */}
      <div className="mx-3.5 rounded-xl overflow-hidden relative h-[88px]">
        <img src={IMG.workout} alt="Workout" className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-black/10 flex flex-col justify-end p-2">
          <p className="text-[9px] font-semibold text-white">Full Body Burn</p>
          <p className="text-[8px] text-white/70">32 min · Intermediate</p>
        </div>
        {!reduced && (
          <motion.div
            className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full bg-primary flex items-center justify-center"
            animate={{ scale: [1, 1.15, 1] }}
            transition={{ duration: 1.8, repeat: Infinity }}
          >
            <Play className="h-2.5 w-2.5 text-primary-foreground fill-primary-foreground ml-px" />
          </motion.div>
        )}
      </div>
      {/* Stats row */}
      <div className="flex gap-2 px-3.5 mt-2">
        <div className="flex-1 rounded-lg bg-white/[0.05] border border-white/10 p-1.5">
          <Footprints className="h-3 w-3 text-primary" />
          <p className="text-[9px] font-semibold text-foreground mt-0.5">8,432</p>
          <p className="text-[7px] text-muted-foreground">steps</p>
        </div>
        <div className="flex-1 rounded-lg bg-white/[0.05] border border-white/10 p-1.5">
          <Flame className="h-3 w-3 text-accent" />
          <p className="text-[9px] font-semibold text-foreground mt-0.5">620</p>
          <p className="text-[7px] text-muted-foreground">kcal</p>
        </div>
        <div className="flex-1 rounded-lg bg-white/[0.05] border border-white/10 p-1.5">
          <Heart className="h-3 w-3 text-destructive" />
          <p className="text-[9px] font-semibold text-foreground mt-0.5">72</p>
          <p className="text-[7px] text-muted-foreground">bpm</p>
        </div>
      </div>
      {/* Weekly chart */}
      <div className="px-3.5 mt-2">
        <p className="text-[8px] text-muted-foreground mb-1">This week</p>
        <div className="flex items-end gap-1 h-8">
          {[40, 65, 50, 80, 60, 95, 70].map((height, i) => (
            <motion.div
              key={i}
              className="flex-1 rounded-sm bg-gradient-to-t from-primary/40 to-primary/80"
              initial={{ height: "15%" }}
              whileInView={{ height: `${height}%` }}
              viewport={{ once: false, amount: 0.4 }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
            />
          ))}
        </div>
      </div>
      {/* Bottom tab bar */}
      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-around py-2.5 bg-black/80 border-t border-white/10 backdrop-blur">
        <Home className="h-3.5 w-3.5 text-primary" />
        <Activity className="h-3.5 w-3.5 text-muted-foreground" />
        <BarChart3 className="h-3.5 w-3.5 text-muted-foreground" />
        <User className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
    </motion.div>

    {/* Second phone screen (gym app) behind right */}
    <motion.div
      className="absolute w-[140px] h-[280px] rounded-[1.8rem] border-4 border-zinc-800/70 bg-black rotate-12 translate-x-24 md:translate-x-32 opacity-50 overflow-hidden"
      animate={reduced ? {} : { rotate: [12, 10, 12] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
    >
      <img src={IMG.gym} alt="Gym app screen" className="w-full h-2/5 object-cover opacity-80" loading="lazy" />
      <div className="p-3 space-y-2">
        <div className="h-2 w-16 rounded-full bg-white/15" />
        <div className="h-2 w-12 rounded-full bg-white/10" />
        <div className="h-9 rounded-lg bg-accent/15 border border-accent/20" />
        <div className="h-9 rounded-lg bg-white/5 border border-white/10" />
      </div>
    </motion.div>

    {/* Floating notification */}
    {!reduced && (
      <motion.div
        className="absolute top-5 right-2 md:right-8 z-20 flex items-center gap-2 rounded-xl bg-black/70 border border-white/15 backdrop-blur px-3 py-2 shadow-xl"
        animate={{ opacity: [0, 1, 1, 0], y: [-10, 0, 0, -8] }}
        transition={{ duration: 6, repeat: Infinity, times: [0, 0.12, 0.85, 1] }}
      >
        <div className="h-6 w-6 rounded-lg bg-primary/20 border border-primary/40 flex items-center justify-center">
          <Bell className="h-3 w-3 text-primary" />
        </div>
        <div>
          <p className="text-[10px] font-semibold text-foreground">New personal best!</p>
          <p className="text-[9px] text-muted-foreground">5km in 24:10</p>
        </div>
      </motion.div>
    )}

    {/* Rating badge */}
    <motion.div
      className="absolute top-24 left-2 md:left-8 z-20 flex items-center gap-1.5 rounded-full bg-black/70 border border-white/15 backdrop-blur px-3 py-1.5 shadow-xl"
      animate={reduced ? {} : { y: [0, -6, 0] }}
      transition={{ duration: 4.2, repeat: Infinity, ease: "easeInOut" }}
    >
      <Star className="h-3 w-3 text-accent fill-accent" />
      <span className="text-[10px] font-semibold text-foreground">4.9</span>
      <span className="text-[9px] text-muted-foreground">on stores</span>
    </motion.div>

    {/* Store chips */}
    <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-2 z-20">
      <motion.div
        className="flex items-center gap-1.5 rounded-full bg-black/70 border border-white/15 backdrop-blur px-3 py-1.5 text-[10px] text-foreground shadow-xl"
        animate={reduced ? {} : { y: [0, -4, 0] }}
        transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
      >
        <Apple className="h-3 w-3 text-foreground" /> App Store
      </motion.div>
      <motion.div
        className="flex items-center gap-1.5 rounded-full bg-black/70 border border-white/15 backdrop-blur px-3 py-1.5 text-[10px] text-foreground shadow-xl"
        animate={reduced ? {} : { y: [0, -4, 0] }}
        transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
      >
        <Play className="h-3 w-3 text-primary fill-primary" /> Google Play
      </motion.div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Panel 5 — Custom Business Systems                                   */
/* ------------------------------------------------------------------ */
const SystemsAnimation = ({ reduced }) => (
  <div className="relative w-full h-[320px] md:h-[360px] overflow-hidden p-5">
    <div className="absolute top-6 left-6 right-6 h-[42%] rounded-xl bg-white/[0.04] border border-white/10 p-4">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
        <BarChart3 className="h-3.5 w-3.5 text-primary" /> Live stock report
      </div>
      <div className="flex items-end gap-2.5 h-[60%]">
        {[45, 70, 55, 88, 62, 95, 74, 100, 68, 82].map((height, i) => (
          <motion.div
            key={i}
            className="flex-1 rounded-t-md bg-gradient-to-t from-primary/30 to-primary/70"
            initial={{ height: "8%" }}
            whileInView={{ height: `${height}%` }}
            viewport={{ once: false, amount: 0.4 }}
            transition={{ duration: 0.8, delay: i * 0.08, ease: "easeOut" }}
          />
        ))}
      </div>
    </div>

    <div className="absolute bottom-10 left-6 right-6 h-[34%] rounded-xl bg-white/[0.04] border border-white/10 overflow-hidden">
      <div className="absolute bottom-5 left-0 right-0 h-1.5 bg-white/10" />
      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-primary/40" />
      {!reduced && (
        <motion.div
          className="absolute left-1/2 -translate-x-1/2 top-0 bottom-6 w-10"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(34,211,238,0.18), transparent)",
          }}
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 1.6, repeat: Infinity }}
        />
      )}
      <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-1 text-[10px] text-primary">
        <ScanLine className="h-3 w-3" /> scanning
      </div>
      {!reduced &&
        [0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="absolute bottom-6 h-10 w-10 rounded-lg bg-accent/15 border border-accent/40 flex items-center justify-center"
            initial={{ left: "-12%" }}
            animate={{ left: ["-12%", "105%"] }}
            transition={{ duration: 6, repeat: Infinity, delay: i * 2, ease: "linear" }}
          >
            <Package className="h-5 w-5 text-accent" />
          </motion.div>
        ))}
      {reduced && (
        <div className="absolute bottom-6 left-8 h-10 w-10 rounded-lg bg-accent/15 border border-accent/40 flex items-center justify-center">
          <Package className="h-5 w-5 text-accent" />
        </div>
      )}
    </div>

    {!reduced &&
      [0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="absolute bottom-[46%] left-1/2 -translate-x-1/2 flex items-center gap-1 text-[11px] text-emerald-400 bg-black/60 border border-emerald-400/30 rounded-full px-2.5 py-1 backdrop-blur z-10"
          animate={{ opacity: [0, 1, 0], y: [8, -6, -16] }}
          transition={{ duration: 2, repeat: Infinity, delay: i * 2 + 2.6, repeatDelay: 4 }}
        >
          <Check className="h-3 w-3" /> SKU-10{i + 4}2 counted
        </motion.div>
      ))}

    <div className="absolute bottom-2 left-6 right-6 text-center text-xs text-muted-foreground">
      Stock count · camera systems · automated reports — built for your workflow
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Showcase Section                                                    */
/* ------------------------------------------------------------------ */
const PANELS = [
  {
    id: "security",
    overline: "Cyber Security",
    title: "Your business, guarded around the clock",
    text: "Firewalls, monitoring, secure servers and safe practices baked into everything we build. Threats get blocked before they ever reach your data.",
    bullets: ["Security audits & hardening", "Safe payments & customer data", "Servers, networks & backups covered"],
    Animation: SecurityAnimation,
  },
  {
    id: "commerce",
    overline: "Selling Online",
    title: "Turn Amazon, eBay, Etsy & Shopify into revenue",
    text: "From FBA and dropshipping to private label and full Shopify stores — we set up the channels, you watch the orders come in.",
    bullets: ["Amazon FBA, Private & White Label", "eBay & Etsy dropshipping setups", "High-converting Shopify stores"],
    Animation: CommerceAnimation,
  },
  {
    id: "web-development",
    overline: "Website Development",
    title: "Websites that look premium and convert",
    text: "Business sites, e-commerce stores and full marketplaces — pixel-perfect on every device, lightning fast, SEO-ready and launched live to the public.",
    bullets: ["Business, personal & e-commerce sites", "Marketplace platforms of any size", "Fast, responsive & SEO optimised"],
    Animation: WebsiteAnimation,
  },
  {
    id: "mobile-apps",
    overline: "Mobile App Development",
    title: "iOS & Android apps your customers love",
    text: "From idea to the App Store and Google Play — beautifully designed, native-quality apps with every feature your business needs, launched and supported.",
    bullets: ["iOS & Android from one project", "App Store & Google Play launch", "Payments, notifications & any feature"],
    Animation: MobileAnimation,
  },
  {
    id: "systems",
    overline: "Custom Systems",
    title: "Describe your business. We build the system.",
    text: "Warehouse stock counts, camera systems, reporting dashboards — whatever your operation needs to run itself, we design and deliver it.",
    bullets: ["Stock count & inventory systems", "Camera & monitoring setups", "Reports that write themselves"],
    Animation: SystemsAnimation,
  },
];

export const Showcase = () => {
  const reduced = useReducedMotion();

  return (
    <section id="showcase" className="scroll-mt-20 py-16 md:py-24" data-testid="showcase-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <p className="text-sm font-medium text-primary tracking-wide uppercase">See It In Action</p>
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-foreground mt-3">
            What working with FCY IT looks like
          </h2>
          <p className="text-muted-foreground mt-4 leading-relaxed">
            Security that never sleeps, stores that sell while you rest, websites and apps
            built to impress, and systems that run your operation — watch them work.
          </p>
        </motion.div>

        <div className="mt-14 space-y-16 md:space-y-24">
          {PANELS.map((panel, index) => {
            const { Animation } = panel;
            const reverse = index % 2 === 1;
            return (
              <div
                key={panel.id}
                className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center"
                data-testid={`showcase-panel-${panel.id}`}
              >
                <motion.div
                  initial={{ opacity: 0, x: reverse ? 32 : -32 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.65, ease: "easeOut" }}
                  className={reverse ? "lg:order-2" : ""}
                >
                  <p className="text-sm font-medium text-accent tracking-wide uppercase">{panel.overline}</p>
                  <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-foreground mt-3">
                    {panel.title}
                  </h3>
                  <p className="text-muted-foreground mt-4 leading-relaxed">{panel.text}</p>
                  <ul className="mt-6 space-y-3">
                    {panel.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-3 text-sm text-foreground/90">
                        <span className="h-5 w-5 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0 mt-0.5">
                          <Check className="h-3 w-3 text-primary" />
                        </span>
                        {bullet}
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={scrollToContact}
                    variant="outline"
                    className="mt-7 rounded-xl border-white/10 bg-white/5 text-foreground hover:bg-white/10 h-11 px-6"
                    data-testid={`showcase-cta-${panel.id}`}
                  >
                    Discuss this <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: reverse ? -32 : 32, scale: 0.96 }}
                  whileInView={{ opacity: 1, x: 0, scale: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.65, ease: "easeOut", delay: 0.1 }}
                  className={`glass-card overflow-hidden ${reverse ? "lg:order-1" : ""}`}
                >
                  <Animation reduced={reduced} />
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
